import { useEffect, useState } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import api from "../../api";

const PERIODS = [
    { label: "30 Days", value: 30 },
    { label: "60 Days", value: 60 },
    { label: "90 Days", value: 90 },
];

function StatCard({ label, value, sub, color }) {
    return (
        <div className={`rounded-xl p-5 ${color} text-white`}>
            <p className="text-sm font-medium opacity-80">{label}</p>
            <p className="text-3xl font-bold mt-1">{value ?? "—"}</p>
            {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
        </div>
    );
}

function TrendBadge({ trend }) {
    if (trend === null || trend === undefined) return <span className="text-secondary-400 text-sm">—</span>;
    if (trend > 0) return <span className="text-emerald-600 font-semibold text-sm">+{trend}%</span>;
    if (trend < 0) return <span className="text-red-500 font-semibold text-sm">{trend}%</span>;
    return <span className="text-secondary-400 text-sm">0%</span>;
}

function ScoreBar({ score, max = 100 }) {
    if (score === null || score === undefined) return <span className="text-secondary-400 text-sm">—</span>;
    const pct = Math.min((score / max) * 100, 100);
    const color = pct >= 70 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-400";
    return (
        <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-secondary-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-sm text-secondary-700 font-medium">{score}%</span>
        </div>
    );
}

function CompetencyList({ title, items, accent, scaleMax }) {
    return (
        <div className="bg-white rounded-xl border border-secondary-100 p-5">
            <h3 className={`text-sm font-semibold uppercase tracking-wide mb-4 ${accent}`}>{title}</h3>
            {items && items.length > 0 ? (
                <ol className="space-y-3">
                    {items.map((item, i) => (
                        <li key={i} className="flex items-center justify-between">
                            <span className="text-sm text-secondary-700 capitalize truncate max-w-[200px]">
                                {i + 1}. {item.skill.replace(/_/g, " ")}
                            </span>
                            <span className={`text-sm font-semibold ${accent}`}>
                                {parseFloat(item.avg).toFixed(2)} <span className="text-secondary-400 font-normal">/ {scaleMax}</span>
                            </span>
                        </li>
                    ))}
                </ol>
            ) : (
                <p className="text-sm text-secondary-400">No data available</p>
            )}
        </div>
    );
}

export default function ProgressReport() {
    const [period, setPeriod] = useState(30);
    const [standard, setStandard] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        api.getProgressReport(period, standard)
            .then((res) => {
                setData(res.data);
                // On first load, sync standard state to what backend resolved
                if (standard === null && res.data.active_standard) {
                    setStandard(res.data.active_standard);
                }
            })
            .catch((err) => setError(err.response?.data?.error || err.message))
            .finally(() => setLoading(false));
    }, [period, standard]);

    const summary = data?.summary || {};
    const therapists = data?.therapists || [];
    const orgComp = data?.org_competencies || {};
    const complexity = data?.complexity_breakdown || {};
    const availableStandards = data?.available_standards || [];
    const scaleMax = orgComp.scale_max || 6;
    const isMixed = availableStandards.length > 1;

    const complexityColors = {
        Low: "bg-emerald-100 text-emerald-700",
        Moderate: "bg-amber-100 text-amber-700",
        High: "bg-red-100 text-red-700",
        Unknown: "bg-secondary-100 text-secondary-500",
    };

    return (
        <DashboardLayout>
            {/* Header */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-heading font-bold text-secondary-900">
                        Progress Report
                    </h1>
                    {data?.date_range && (
                        <p className="text-secondary-500 text-sm mt-1">
                            {data.date_range.from} — {data.date_range.to}
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Evaluation standard dropdown — only shown when org has mixed standards */}
                    {isMixed && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-secondary-500 font-medium">Evaluation Scale</span>
                            <select
                                value={standard || ""}
                                onChange={(e) => setStandard(e.target.value || null)}
                                className="text-sm border border-secondary-200 rounded-lg px-3 py-2 bg-white text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                            >
                                {availableStandards.map((s) => (
                                    <option key={s.key} value={s.key}>
                                        {s.label} ({s.count} sessions)
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Period selector */}
                    {PERIODS.map((p) => (
                        <button
                            key={p.value}
                            onClick={() => setPeriod(p.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${period === p.value
                                    ? "bg-primary-600 text-white"
                                    : "bg-white border border-secondary-200 text-secondary-600 hover:bg-secondary-50"
                                }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-24 rounded-xl bg-secondary-100 animate-pulse" />
                    ))}
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                        <StatCard
                            label="Sessions Completed"
                            value={summary.total_sessions}
                            sub={`${summary.active_therapists} active clinician${summary.active_therapists !== 1 ? "s" : ""}`}
                            color="bg-gradient-to-br from-blue-500 to-blue-700"
                        />
                        <StatCard
                            label="Sessions Flagged"
                            value={summary.flagged_sessions}
                            sub={`${summary.flag_rate}% flag rate`}
                            color="bg-gradient-to-br from-amber-500 to-orange-600"
                        />
                        <StatCard
                            label="Reviews Resolved"
                            value={summary.reviewed_sessions}
                            sub={`${summary.review_resolution_rate}% resolution rate`}
                            color="bg-gradient-to-br from-emerald-500 to-emerald-700"
                        />
                        <StatCard
                            label="Org Avg Score"
                            value={orgComp.overall_avg != null ? `${orgComp.overall_avg}%` : "—"}
                            sub="across all competencies"
                            color="bg-gradient-to-br from-violet-500 to-violet-700"
                        />
                    </div>

                    {/* Therapist Table */}
                    <div className="bg-white rounded-xl border border-secondary-200 mb-8 overflow-hidden">
                        <div className="px-5 py-4 border-b border-secondary-100">
                            <h2 className="text-base font-semibold text-secondary-900">Clinician Performance</h2>
                            <p className="text-xs text-secondary-400 mt-0.5">
                                Ranked by avg competency score. Trend = change vs prior period.
                                {isMixed && (
                                    <span className="ml-1">
                                        Skill gap based on <span className="font-medium text-secondary-600">
                                            {availableStandards.find(s => s.key === standard)?.label || standard}
                                        </span> sessions only.
                                    </span>
                                )}
                            </p>
                        </div>
                        {therapists.length === 0 ? (
                            <p className="text-sm text-secondary-400 p-6">No completed sessions in this period.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-secondary-50 text-secondary-500 uppercase text-xs">
                                            <th className="text-left px-5 py-3 font-medium">Clinician</th>
                                            <th className="text-center px-4 py-3 font-medium">Sessions</th>
                                            <th className="text-left px-4 py-3 font-medium">Avg Score</th>
                                            <th className="text-center px-4 py-3 font-medium">Trend</th>
                                            <th className="text-left px-4 py-3 font-medium">Top Gap</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-secondary-50">
                                        {therapists.map((t) => (
                                            <tr key={t.id} className="hover:bg-secondary-50 transition-colors">
                                                <td className="px-5 py-3 font-medium text-secondary-800">{t.name}</td>
                                                <td className="px-4 py-3 text-center text-secondary-600">{t.sessions}</td>
                                                <td className="px-4 py-3">
                                                    <ScoreBar score={t.avg_score} />
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <TrendBadge trend={t.trend} />
                                                </td>
                                                <td className="px-4 py-3 text-secondary-500 capitalize text-xs">
                                                    {t.top_gap ? (
                                                        <span>
                                                            {t.top_gap.skill.replace(/_/g, " ")}
                                                            <span className="text-red-400 font-medium ml-1">
                                                                ({parseFloat(t.top_gap.avg).toFixed(2)} / {scaleMax})
                                                            </span>
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Competencies + Complexity */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <CompetencyList
                            title="Strongest Competencies"
                            items={orgComp.strongest}
                            accent="text-emerald-600"
                            scaleMax={scaleMax}
                        />
                        <CompetencyList
                            title="Weakest Competencies"
                            items={orgComp.weakest}
                            accent="text-red-500"
                            scaleMax={scaleMax}
                        />
                        <div className="bg-white rounded-xl border border-secondary-100 p-5">
                            <h3 className="text-sm font-semibold uppercase tracking-wide text-secondary-500 mb-4">
                                Case Complexity
                            </h3>
                            <div className="space-y-3">
                                {Object.entries(complexity).length === 0 ? (
                                    <p className="text-sm text-secondary-400">No data</p>
                                ) : (
                                    Object.entries(complexity)
                                        .sort((a, b) => {
                                            const order = ["High", "Moderate", "Low", "Unknown"];
                                            return order.indexOf(a[0]) - order.indexOf(b[0]);
                                        })
                                        .map(([level, count]) => (
                                            <div key={level} className="flex items-center justify-between">
                                                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${complexityColors[level] || complexityColors.Unknown}`}>
                                                    {level}
                                                </span>
                                                <span className="text-sm font-semibold text-secondary-700">{count}</span>
                                            </div>
                                        ))
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </DashboardLayout>
    );
}
